import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, UserPen, LogOut, X, Eye, EyeOff, Briefcase, MessageCircle } from "lucide-react";
import { getUserInfoFromToken, changePassword, changeUsername, isAgent } from "../services/auth";
import { toast } from "react-toastify";

// ── small reusable modal shell ────────────────────────────────────────────────
const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50">
    <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <h2 className="text-base font-semibold text-gray-800">{title}</h2>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  </div>
);

// ── password field with show/hide toggle ─────────────────────────────────────
const PasswordField = ({ label, value, onChange, placeholder }) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

// ── Change Password Modal ─────────────────────────────────────────────────────
const ChangePasswordModal = ({ onClose }) => {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    if (form.next.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await changePassword(form.current, form.next);
      toast.success("Password changed successfully");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Change Password" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <PasswordField
          label="Current Password"
          value={form.current}
          onChange={set("current")}
          placeholder="Enter current password"
        />
        <PasswordField
          label="New Password"
          value={form.next}
          onChange={set("next")}
          placeholder="At least 6 characters"
        />
        <PasswordField
          label="Confirm New Password"
          value={form.confirm}
          onChange={set("confirm")}
          placeholder="Repeat new password"
        />
        <div className="flex justify-end gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 transition-colors"
          >
            {loading ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ── Change Username Modal ─────────────────────────────────────────────────────
const ChangeUsernameModal = ({ currentName, onClose }) => {
  const [value, setValue] = useState(currentName || "");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!value.trim()) {
      toast.error("Username cannot be empty");
      return;
    }
    setLoading(true);
    try {
      const data = await changeUsername(value.trim());
      // replace token so the new name is reflected immediately
      if (data.token) localStorage.setItem("token", data.token);
      toast.success("Username updated successfully");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to update username");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Change Username" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            New Username
          </label>
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Enter new username"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex justify-end gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 transition-colors"
          >
            {loading ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ── Main UserMenu component ───────────────────────────────────────────────────
const UserMenu = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null); // "password" | "username" | null
  const menuRef = useRef(null);

  const agentRole = isAgent();
  const userInfo = getUserInfoFromToken();
  const userName = userInfo?.userName || "User";
  const userContact = userInfo?.phone || userInfo?.email || null;
  const waPhone = userInfo?.waPhone || null;
  const initials = userName.slice(0, 2).toUpperCase();

  // close dropdown when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleLogout = () => {
    setOpen(false);
    if (window.confirm("Are you sure you want to logout?")) {
      localStorage.removeItem("token");
      navigate("/");
    }
  };

  const openModal = (type) => {
    setOpen(false);
    setModal(type);
  };

  const menuItems = [
    !agentRole && {
      label: "Business Profile",
      icon: Briefcase,
      onClick: () => { setOpen(false); navigate("/business-profile"); },
    },
    !agentRole && {
      label: "Change Password",
      icon: KeyRound,
      onClick: () => openModal("password"),
    },
    !agentRole && {
      label: "Change Username",
      icon: UserPen,
      onClick: () => openModal("username"),
    },
    {
      label: "Logout",
      icon: LogOut,
      onClick: handleLogout,
      danger: true,
    },
  ].filter(Boolean);

  return (
    <>
      <div className="relative" ref={menuRef}>
        {/* Avatar button */}
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors group"
          title={userName}
        >
          <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
            {initials}
          </div>
          <span className="hidden sm:block text-sm font-medium text-gray-700 max-w-[120px] truncate group-hover:text-gray-900">
            {userName}
          </span>
        </button>

        {/* Dropdown */}
        {open && (
          <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-[100]">
            {/* name header */}
            <div className="px-4 py-2.5 border-b border-gray-100">
              <p className="text-xs text-gray-400">Signed in as</p>
              <p className="text-sm font-semibold text-gray-800 truncate">{userName}</p>
              {userContact && (
                <p className="text-xs text-gray-500 mt-0.5">{userContact}</p>
              )}
              {waPhone && (
                <div className="flex items-center gap-1 mt-1">
                  <MessageCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                  <p className="text-xs text-green-600 font-medium">{waPhone}</p>
                </div>
              )}
            </div>

            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.label}
                  onClick={item.onClick}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-gray-50 ${
                    item.danger ? "text-red-600" : "text-gray-700"
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {item.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Modals */}
      {modal === "password" && (
        <ChangePasswordModal onClose={() => setModal(null)} />
      )}
      {modal === "username" && (
        <ChangeUsernameModal
          currentName={userName}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
};

export default UserMenu;
